// MA-107: READ-ONLY view of "is the MA-15 outbox holding writes while we are
// online" — for a calm banner line. It adds no data-layer behaviour: it only
// reads the same stores the drain reads.
//
// WHY "STALE", not "non-empty": a SUCCESSFUL online write is NOT marked
// confirmed at write time — only outboxDrain marks rows confirmed, on its next
// tick (mount, verified reconnect, tab foreground, or the 5-minute periodic
// tick). So a row is routinely "unconfirmed" for up to PERIODIC_DRAIN_INTERVAL_MS
// after perfectly healthy play. A row still unconfirmed one full periodic tick
// (+ margin) later means the drain ran and could NOT land it (Supabase 5xx,
// RLS/grant error, auth refresh failure — states where reachability still reads
// "online"). Queued pending sessions are different: they are only ever
// enqueued AFTER a failed save, so any queued session is genuinely held.
//
// HONESTY of the copy "we'll keep trying": outboxDrain retries on mount, on
// every verified-online transition (useOutboxDrain -> registerOnlineCallback),
// on tab foreground, and every PERIODIC_DRAIN_INTERVAL_MS (5 min).

import { PERIODIC_DRAIN_INTERVAL_MS, DRAINABLE_MILESTONE_TYPES } from "./outboxDrain";
import { loadUnconfirmedAttempts } from "./localAttemptStore";
import { loadLocalMilestones, loadConfirmedMilestoneKeys, milestoneDedupKey } from "./localMilestoneStore";
import { loadPendingSessions } from "./localSessionStore";

/** An unconfirmed row older than one periodic tick + 1 min has been retried. */
export const OUTBOX_STALE_MS = PERIODIC_DRAIN_INTERVAL_MS + 60_000;
/** The condition must persist this long before the line shows (no flicker). */
export const OUTBOX_SHOW_AFTER_MS = 30_000;
/** How often the hook re-reads the (localStorage) stores. */
export const OUTBOX_POLL_MS = 15_000;

export interface PendingDeps {
  loadUnconfirmedAttempts: (childId: string, limit: number) => Array<{ timestamp?: string }>;
  loadLocalMilestones: (childId: string) => Array<{ eventType: string; eventAt?: string }>;
  /** Read ONCE per countHeldWrites call (not per milestone). */
  loadConfirmedMilestoneKeys: (childId: string) => Set<string>;
  milestoneKey: (e: never) => string;
  loadPendingSessions: () => Array<{ studentId: string }>;
}

// MA-866: exported (unchanged) so the parent kid card's save-state line reads
// the very same per-kid store readers countHeldWrites reads.
export const realDeps: PendingDeps = {
  loadUnconfirmedAttempts: (c, l) => loadUnconfirmedAttempts(c, l),
  loadLocalMilestones: (c) => loadLocalMilestones(c, undefined, 500),
  loadConfirmedMilestoneKeys: (c) => loadConfirmedMilestoneKeys(c),
  milestoneKey: (e) => milestoneDedupKey(e as never),
  loadPendingSessions: () => loadPendingSessions(),
};

function ageOk(iso: string | undefined, nowMs: number): boolean {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) && nowMs - t >= OUTBOX_STALE_MS;
}

/** Count writes the outbox has been unable to land for a full retry cycle. */
export function countHeldWrites(
  localChildId: string | null | undefined,
  canonicalStudentId: string | null | undefined,
  nowMs: number,
  deps: PendingDeps = realDeps,
): number {
  if (!localChildId) return 0;
  let n = 0;
  try {
    n += deps.loadUnconfirmedAttempts(localChildId, 1000).filter((a) => ageOk(a.timestamp, nowMs)).length;
    // Cheap predicates first; the confirmed set is read once, only if some
    // milestone survives them, and then shared by every row.
    const candidates = deps
      .loadLocalMilestones(localChildId)
      .filter((e) => DRAINABLE_MILESTONE_TYPES.has(e.eventType) && ageOk(e.eventAt, nowMs));
    if (candidates.length > 0) {
      const confirmed = deps.loadConfirmedMilestoneKeys(localChildId);
      n += candidates.filter((e) => !confirmed.has(deps.milestoneKey(e as never))).length;
    }
    if (canonicalStudentId) {
      n += deps.loadPendingSessions().filter((s) => s.studentId === canonicalStudentId).length;
    }
  } catch {
    return 0; // best-effort read; never throw into a render path
  }
  return n;
}

/** Debounce state machine: returns the new "held since" timestamp (null when
 *  nothing is held). */
export function nextHeldSince(prev: number | null, heldCount: number, nowMs: number): number | null {
  if (heldCount <= 0) return null;
  return prev ?? nowMs;
}

/** Visible only once the held condition has persisted OUTBOX_SHOW_AFTER_MS. */
export function holdingLineVisible(heldSince: number | null, nowMs: number): boolean {
  return heldSince !== null && nowMs - heldSince >= OUTBOX_SHOW_AFTER_MS;
}

/** Pure enable gate for the holding poll — mirrors useOutboxDrain's own guard
 *  (real session + BOTH ids). Off for a local-only kid (no canonical student id,
 *  e.g. no consent yet): the drain never runs for them, so "we'll keep trying"
 *  would be false. */
export function holdingEnabled(
  persistEnabled: boolean,
  localChildId: string | null | undefined,
  canonicalStudentId: string | null | undefined,
): boolean {
  return !!persistEnabled && !!localChildId && !!canonicalStudentId;
}
